import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Lightbulb, Hand, Link2, Quote, BookOpenCheck } from 'lucide-react';
import 'katex/dist/katex.min.css';
import SlideShell from './SlideShell';
import { LearnSlideContent } from '@/types/course-outline';

interface CalloutSection {
  type: 'watch-outs' | 'guided-practice' | 'extras' | 'citations' | 'learning-targets';
  content: string;
}

const parseCallouts = (markdown: string): { mainContent: string; callouts: CalloutSection[] } => {
  const callouts: CalloutSection[] = [];
  const lines = markdown.split('\n');
  let mainContent: string[] = [];
  let currentCallout: CalloutSection | null = null;
  let calloutLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if this line starts a callout section
    if (line.startsWith('#### Watch-outs') || line.startsWith('#### Watch-Outs')) {
      if (currentCallout) {
        currentCallout.content = calloutLines.join('\n').trim();
        callouts.push(currentCallout);
      }
      currentCallout = { type: 'watch-outs', content: '' };
      calloutLines = [];
    } else if (line.startsWith('#### Guided Practice')) {
      if (currentCallout) {
        currentCallout.content = calloutLines.join('\n').trim();
        callouts.push(currentCallout);
      }
      currentCallout = { type: 'guided-practice', content: '' };
      calloutLines = [];
    } else if (line.startsWith('#### Extras') || line.startsWith('#### Media')) {
      if (currentCallout) {
        currentCallout.content = calloutLines.join('\n').trim();
        callouts.push(currentCallout);
      }
      currentCallout = { type: 'extras', content: '' };
      calloutLines = [];
    } else if (line.startsWith('#### Citations')) {
      if (currentCallout) {
        currentCallout.content = calloutLines.join('\n').trim();
        callouts.push(currentCallout);
      }
      currentCallout = { type: 'citations', content: '' };
      calloutLines = [];
    } else if (line.startsWith('#### Learning Targets') || line.startsWith('#### Learning Objectives')) {
      if (currentCallout) {
        currentCallout.content = calloutLines.join('\n').trim();
        callouts.push(currentCallout);
      }
      currentCallout = { type: 'learning-targets', content: '' };
      calloutLines = [];
    } else if (currentCallout) {
      calloutLines.push(line);
    } else {
      mainContent.push(line);
    }
  }

  // Add the last callout if exists
  if (currentCallout && calloutLines.length > 0) {
    currentCallout.content = calloutLines.join('\n').trim();
    callouts.push(currentCallout);
  }

  return { mainContent: mainContent.join('\n'), callouts };
};

const Callout = ({ type, children }: { type: CalloutSection['type']; children: React.ReactNode }) => {
  const config = {
    'watch-outs': {
      icon: Lightbulb,
      label: 'Watch-outs',
      accentColor: '#F59E0B'
    },
    'guided-practice': {
      icon: Hand,
      label: 'Guided Practice',
      accentColor: '#3B82F6'
    },
    'extras': {
      icon: Link2,
      label: 'Extras & Media',
      accentColor: '#A855F7'
    },
    'citations': {
      icon: Quote,
      label: 'Citations',
      accentColor: '#6B7280'
    },
    'learning-targets': {
      icon: BookOpenCheck,
      label: 'Learning Targets',
      accentColor: '#10B981'
    }
  };

  const { icon: Icon, label, accentColor } = config[type];

  return (
    <div className="bg-white rounded-[15px] border border-black/10 px-5 py-4 my-4">
      <div className="flex gap-3">
        <Icon className="flex-shrink-0 mt-0.5" size={18} style={{ color: '#666666' }} strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-2 text-foreground">{label}</div>
          <div className="text-base text-foreground/80 leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

interface LearnSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: LearnSlideContent;
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const LearnSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: LearnSlideProps) => {
  const { mainContent, callouts } = parseCallouts(content.markdown);

  const markdownComponents = {
    // Headings
    h1: ({ children }: any) => <h1 className="text-3xl font-bold leading-tight mb-4 text-foreground">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-2xl font-semibold leading-snug mb-3 text-foreground">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xl font-semibold leading-snug mb-2 text-foreground">{children}</h3>,
    
    // Text elements
    p: ({ children }: any) => <p className="text-base text-foreground/90 leading-relaxed mb-6">{children}</p>,
    ul: ({ children }: any) => <ul className="list-disc list-outside ml-5 space-y-3 mb-4 text-base text-foreground marker:text-foreground/60">{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal list-outside ml-5 space-y-3 mb-4 text-base text-foreground marker:text-foreground/60">{children}</ol>,
    li: ({ children }: any) => <li className="text-base text-foreground/90 leading-relaxed pl-2">{children}</li>,
    strong: ({ children }: any) => <strong className="font-semibold text-foreground">{children}</strong>,
    em: ({ children }: any) => <em className="italic text-foreground">{children}</em>,
    
    // Code blocks with syntax highlighting
    code({ node, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const isInline = !language;
      
      return !isInline && language ? (
        <div className="my-4 rounded-lg overflow-hidden">
          <SyntaxHighlighter
            style={oneDark as any}
            language={language}
            PreTag="div"
            className="text-xs"
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      ) : (
        <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-[13px] font-mono text-foreground" {...props}>
          {children}
        </code>
      );
    },
    
    // Tables
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className="bg-gray-50 dark:bg-gray-800">{children}</thead>
    ),
    tbody: ({ children }: any) => <tbody>{children}</tbody>,
    tr: ({ children }: any) => (
      <tr className="border-b border-gray-200 dark:border-gray-700">{children}</tr>
    ),
    th: ({ children }: any) => (
      <th className="px-4 py-2 text-left font-semibold text-sm text-foreground border border-gray-300 dark:border-gray-700">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="px-4 py-2 text-sm text-foreground/90 border border-gray-300 dark:border-gray-700">
        {children}
      </td>
    ),
    
    // Blockquotes
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 dark:bg-blue-900/20 text-base italic text-foreground/80">
        {children}
      </blockquote>
    ),
    
    // Horizontal rules
    hr: () => (
      <hr className="my-6 border-gray-300 dark:border-gray-700" />
    ),
  };

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={!!onMarkComplete}
      isCompleted={isCompleted}
      onMarkComplete={onMarkComplete}
    >
      <h1 className="font-heading text-3xl mb-6 text-foreground">
        {title}
      </h1>

      <div className="flex gap-9">
        <div className="flex-1">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={markdownComponents}
          >
            {mainContent}
          </ReactMarkdown>

          {callouts.map((callout, index) => (
            <Callout key={index} type={callout.type}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={markdownComponents}
              >
                {callout.content}
              </ReactMarkdown>
            </Callout>
          ))}
        </div>
        {content.imageUrl && (
          <img 
            src={content.imageUrl} 
            alt=""
            className="w-[200px] h-[200px] object-cover rounded-[15px] border border-[#f0f0f0]"
          />
        )}
      </div>
    </SlideShell>
  );
};

export default LearnSlide;
